import { Fragment } from 'react'

import { Box, Grid } from '@sequencehq/core-components'

import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { ConditionPropertyEditorComponent } from 'Automations/nodeDefinitions/conditions'
import type { QuoteComparisonSubOperator } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { filterErrorsByField } from 'Automations/automationEditor/utils'
import {
  comparisonOptionsFor,
  isQuoteComparisonField,
  parseExpression
} from '../quoteComparison'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import { ReviewNodeSelector } from './ReviewNodeSelector'
import {
  readRow,
  usePreviousApprovalOperatorState
} from './PreviousApprovalPropertyEditor.helpers'

/**
 * Property-level editor for PREVIOUS_APPROVAL. Owns the review-step picker, the
 * field dropdown, and the sub-row the field offers — which waits on both picks
 * above it, since its labels agree with the chosen field.
 *
 *   +----------------+----------------+
 *   | review step    | field          |   field: "All prices"
 *   +----------------+----------------+
 *   | is / is not                     |
 *   +---------------------------------+
 *
 * Pricing's comparisons sit in the field dropdown beside the other fields, so
 * one pick names what is compared. The row still stores them apart.
 *
 * What the row effectively holds — the defaults, and the fallback when a sibling
 * has taken a comparison — is decided and committed by
 * `usePreviousApprovalOperatorState`. This component renders those values and
 * writes the user's picks.
 */
export const PreviousApprovalPropertyEditor: ConditionPropertyEditorComponent =
  ({
    'data-testid': dataTestId,
    nodes,
    operator,
    value,
    onChange,
    operators,
    siblingConditions,
    validationErrors,
    focusOnInitialLoad
  }) => {
    const row = readRow(value)

    const {
      reviewNodeOptions,
      reviewNodeId,
      expressionOptions,
      expressionDisabled,
      expression,
      subOperator
    } = usePreviousApprovalOperatorState({
      nodes,
      operator,
      row,
      operators,
      siblingConditions,
      onChange
    })

    // Each dropdown writes the whole value, so it carries the others through. The
    // comparison is pricing's alone, so a field without comparisons stores none.
    const update = (next: {
      reviewNodeId?: string
      subOperator?: QuoteComparisonSubOperator
    }) =>
      onChange({
        value: {
          reviewNodeId: next.reviewNodeId ?? reviewNodeId ?? '',
          operator: next.subOperator ?? subOperator,
          comparison: row.comparison
        }
      })

    // One pick carries both halves: the field the row compares on, and — for
    // pricing — which comparison. What is stored keeps them apart.
    const onFieldChange = (selected: unknown) => {
      const { field, comparison } = parseExpression(String(selected))
      onChange({
        operator: field,
        value: {
          reviewNodeId: reviewNodeId ?? '',
          operator: subOperator,
          comparison
        }
      })
    }

    const reviewNodeSelected = Boolean(reviewNodeId)
    const fieldSelected = isQuoteComparisonField(operator)

    return (
      <Fragment>
        <Grid
          gridTemplateColumns="1fr 1fr"
          gap="8px"
          gridTemplateAreas='"review operator" "subOperator subOperator"'
        >
          <Box gridArea="review">
            <ReviewNodeSelector
              data-testid={`${dataTestId}.review`}
              options={reviewNodeOptions}
              value={reviewNodeId ?? ''}
              onChange={selected => update({ reviewNodeId: selected })}
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'reviewNodeId'
              )}
              initiallyOpen={focusOnInitialLoad}
            />
          </Box>
          <Box gridArea="operator">
            <InputSelectControlField
              data-testid={`${dataTestId}.operator`}
              placeholder="Select field..."
              options={expressionOptions}
              value={expression}
              disabled={expressionDisabled}
              onChange={onFieldChange}
              validationErrors={validationErrors?.operator}
            />
          </Box>
          {reviewNodeSelected && fieldSelected && (
            <Box gridArea="subOperator" width={SUB_OPERATOR_COLUMN_WIDTH}>
              <InputSelectControlField
                data-testid={`${dataTestId}.subOperator`}
                value={subOperator}
                onChange={selected =>
                  update({
                    subOperator: selected as QuoteComparisonSubOperator
                  })
                }
                options={comparisonOptionsFor({ operator, value })}
                placeholder="Select..."
                validationErrors={filterErrorsByField(
                  validationErrors?.value,
                  'operator'
                )}
              />
            </Box>
          )}
        </Grid>
      </Fragment>
    )
  }
